import { LaunchServiceOptions, ServiceRoutingPlugin } from '@jarvis/jweb-core'
import { Entry } from '../interface/controllerInterface'
import { Logger } from '../utils/helperMethods'

export interface LaunchParam {
  /**
   * The Onboarding Director Id that will be send CreateSessionPayload.
   * Required.
   */
  clientId: string

  /**
   * If present, will mock the Onboarding Director response to have this as the next
   * service.
   * Optional.
   */
  nextService: string

  /**
   * If present, will load the Onboarding Center from the given URL instead the default
   * path.
   * Optional.
   */
  onboardingCenter: string

  /**
   * If present, will load the be used to fill the OnboardingContext. Entry property
   * inside the CreateSessionPayload.
   * Optional.
   */
  onboardingContextEntry: Entry

  /**
   * Biz model to send to Onboarding Director.
   * Optional.
   */
  bizModel: string

  /**
   * Offer to send to Onboarding Director.
   * Optional.
   */
  offer: number

  /**
   * Defines if the application is running in debug mode. Will pass that to DRPs `ResourceProvider`.
   * Required.
   * Default: false
   */
  isDebugMode: boolean

  /**
   * Defines if the values from the object were loaded from `Service Routing` or query parameters
   */
  wasLoadedFromServiceRouting: boolean

  /**
   * Application instance ID
   * Optional.
   */
  appInstanceId: string
}

export class LaunchOptionsLoader {
  /**
   * Constructor class.
   * @param ServiceRouting Reference to the Service Routing Plugin
   */
  constructor(serviceRouting: ServiceRoutingPlugin) {
    this.serviceRouting = serviceRouting
  }

  /**
   * Loads the required data for Bootstrap to run.
   * Will receive values from both Service Routing LaunchServiceOptions or query parameters, prioritizing the query parameters.
   */
  public loadLaunchOptions = (): LaunchParam => {
    //
    if (!this.initializeDone) {
      const message = 'LaunchOptionsLoader.initialize method was not called'
      Logger.strError(message)
      throw new Error(message)
    }
    this.loadRequiredParam()
    this.loadOptionalParam()

    return this.launchParam
  }

  /**
   * Initialize URLSearchParams and ServiceInstanceLaunchOptions entry point components.
   * It must executed before of called of loadLaunchOptions method
   * @param ServiceRouting Reference to the Service Routing Plugin
   */
  public initialize = async () => {
    // Load query parameters
    this.params = new URLSearchParams(window.location.search)
    Logger.strLog('URL Query params:', this.params.toString())

    // If query parameters are empty, load from SR
    if (this.params.toString().length <= 0) {
      Logger.log('No query parameters, calling getServiceInstanceLaunchOptions')
      await this.launchFromServiceRouting()
    }

    this.initializeDone = true
  }

  private launchFromServiceRouting = async () => {
    //
    try {
      const launchOptionsLoader = (await this.serviceRouting.getServiceInstanceLaunchOptions()) as LaunchServiceOptions
      this.serviceOptions =
        launchOptionsLoader.serviceOptions != undefined
          ? launchOptionsLoader.serviceOptions
          : {}
      // Since it was loaded from SR, set value to true
      this.launchParam.wasLoadedFromServiceRouting = true
      Logger.strLog(
        'Values loaded from getServiceInstanceLaunchOptions: ',
        this.serviceOptions
      )
    } catch (error) {
      Logger.error(
        'getServiceInstanceLaunchOptions caused an exception that was catch',
        error
      )
    }
  }

  private loadRequiredParam = (): void => {
    const odIdKey = 'odId'
    if (!this.serviceOptions[odIdKey] && !this.params.has(odIdKey))
      throw new Error('Required odId is missing')

    this.launchParam.clientId =
      this.serviceOptions[odIdKey] || this.params.get(odIdKey)
    Logger.strLog('odId: ', this.launchParam.clientId)
  }

  private loadOptionalParam = (): void => {
    const nextServiceKey = 'nextService'
    this.launchParam.nextService =
      this.serviceOptions[nextServiceKey] || this.params.get(nextServiceKey)
    Logger.strDebug('nextServiceQueryParam: ', this.launchParam.nextService)

    const onboardingCenterKey = 'onboardingCenter'
    this.launchParam.onboardingCenter =
      this.serviceOptions[onboardingCenterKey] ||
      this.params.get(onboardingCenterKey)
    Logger.strDebug(
      'onboardingCenterQueryParam: ',
      this.launchParam.onboardingCenter
    )

    this.launchParam.onboardingContextEntry = (this.serviceOptions['entry'] ||
      this.params.get('entry')) as Entry
    Logger.strDebug(
      'onboardingContextEntryQueryParam: ',
      this.launchParam.onboardingContextEntry
    )

    const bizModelKey = 'bizModel'
    this.launchParam.bizModel =
      this.serviceOptions[bizModelKey] || this.params.get(bizModelKey)
    Logger.strDebug('bizModelParam: ', this.launchParam.bizModel)

    const offerKey = 'offer'
    const offer = parseInt(
      this.serviceOptions[offerKey] || this.params.get(offerKey)
    )
    if (offer) {
      this.launchParam.offer = offer
    }
    Logger.debug('offerParam: ', this.launchParam.offer)

    const appInstanceIdKey = 'appInstanceId'
    this.launchParam.appInstanceId =
      this.serviceOptions[appInstanceIdKey] || this.params.get(appInstanceIdKey)
    Logger.strDebug('appInstanceIdParam: ', this.launchParam.appInstanceId)

    const isDebugModeKey = 'isDebugMode'
    const isDebugModeValue =
      this.serviceOptions[isDebugModeKey] || this.params.get(isDebugModeKey)
    this.launchParam.isDebugMode = isDebugModeValue === 'true'
    Logger.debug('isDebugModeParam: ', this.launchParam.isDebugMode)
  }

  private initializeDone = false
  private launchParam: LaunchParam = {
    clientId: null,
    nextService: null,
    onboardingCenter: null,
    onboardingContextEntry: Entry.PORTAL_OOBE,
    bizModel: undefined,
    offer: undefined,
    isDebugMode: false,
    wasLoadedFromServiceRouting: false,
    appInstanceId: undefined
  }
  private serviceOptions = {}
  private params: URLSearchParams
  private readonly serviceRouting: ServiceRoutingPlugin
}
